import writeXlsxFile from "write-excel-file";
import {
  getSalesConsolidated,
  getSalesExportDetails,
} from "../../../../utils/services/sales";
import { formatDocument, formatMobile } from "./formatters";
import { buildAttendeesSheet, productsSchema } from "./exportSheets";
// toast
import { toast } from "react-toastify";

const FILE_NAME = "vendasConsolidadas.xlsx";
const DATE_FORMAT = "dd/mm/yyyy HH:mm";

// A biblioteca converte a data para o número do Excel a partir do horário UTC
// (`getTime()`), enquanto a tela mostra a hora no fuso do navegador. Desloca o instante
// pelo offset local para a planilha exibir a mesma hora do grid e do modal de detalhes.
// Sem data, devolve null (célula vazia) — Date inválido viraria um número sem sentido.
export function excelDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000);
}

// Abre a caixa "salvar como" pela API de arquivos do navegador (Chrome/Edge). Precisa
// ser chamada logo no clique, antes de qualquer requisição: a API exige uma ação
// recente do usuário e, depois de vários awaits, passa a recusar a chamada.
// Devolve o handle do arquivo, ou null se o usuário cancelou.
async function pickSaveLocation() {
  try {
    return await window.showSaveFilePicker({
      suggestedName: FILE_NAME,
      types: [
        {
          description: "Planilha Excel",
          accept: {
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
              [".xlsx"],
          },
        },
      ],
    });
  } catch (error) {
    // AbortError = usuário fechou a caixa sem salvar
    if (error?.name === "AbortError") return null;
    throw error;
  }
}

// grava o conteúdo no arquivo escolhido pelo usuário
async function writeToHandle(handle, blob) {
  const writable = await handle.createWritable();
  await writable.write(blob);
  await writable.close();
}

// Download comum (navegadores sem a API). Se o navegador abrir a caixa "salvar como",
// a janela perde o foco; espera o foco voltar antes de considerar concluído, para o
// aviso de "finalizado" não aparecer por trás da caixa.
async function downloadWithAnchor(blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = FILE_NAME;
  link.click();

  await new Promise((resolve) => {
    const done = () => {
      window.removeEventListener("focus", done);
      resolve();
    };
    setTimeout(() => {
      if (document.hasFocus()) done();
      else window.addEventListener("focus", done);
    }, 300);
  });

  URL.revokeObjectURL(url);
}

// atualiza a barra de progresso do modal de exportação (WaitExport)
function setProgress(current, total) {
  const percentage = `${((current / total) * 100).toFixed(0)}%`;
  const progressBar = document.querySelector(".export-progress");
  const percentageBar = document.querySelector(".export-percentage");
  if (progressBar) progressBar.style.width = percentage;
  if (percentageBar) percentageBar.innerHTML = percentage;
}

// Colunas da aba "Vendas". Recebe os tradutores de tipo e status usados na tela.
export function buildSalesSchema({ getTypePayment, getStatusPayment }) {
  return [
    {
      column: "ID",
      type: String,
      value: (sale) => sale.id,
    },
    // dados do comprador informados no checkout (vazios quando não há)
    {
      column: "Nome",
      type: String,
      value: (sale) => sale.name ?? "",
    },
    {
      column: "CPF/CNPJ",
      type: String,
      value: (sale) => formatDocument(sale.document),
    },
    {
      column: "Email Pagador",
      type: String,
      value: (sale) => sale.email,
    },
    {
      column: "Celular",
      type: String,
      value: (sale) => formatMobile(sale.mobile),
    },
    {
      column: "Endereço",
      type: String,
      value: (sale) => sale.address ?? "",
    },
    {
      column: "Tipo Pagamento",
      type: String,
      value: (sale) => getTypePayment(sale.type),
    },
    // número real com formato de moeda, para o Excel conseguir somar
    {
      column: "Valor Total",
      type: Number,
      format: '"R$" #,##0.00',
      value: (sale) => {
        const amount = Number(sale.amount);
        return Number.isNaN(amount) ? null : amount;
      },
    },
    // datas como célula de data de verdade, para o Excel ordenar e filtrar por data
    {
      column: "Data Criação",
      type: Date,
      format: DATE_FORMAT,
      value: (sale) => excelDate(sale.created_at),
    },
    {
      column: "Data Pagamento",
      type: Date,
      format: DATE_FORMAT,
      value: (sale) => excelDate(sale.updated_at),
    },
    {
      column: "Status",
      type: String,
      value: (sale) => getStatusPayment(sale.status).text,
    },
  ];
}

export async function createXlsx({
  token,
  search,
  eventId,
  limit,
  columnSearch,
  countData,
  setFinishExport,
  setExportInProgress,
  getTypePayment,
  getStatusPayment,
}) {
  const schema = buildSalesSchema({ getTypePayment, getStatusPayment });

  // fecha o modal de espera sem o aviso de "finalizado" (cancelamento ou erro)
  const closeQuietly = () => {
    setFinishExport(false);
    setExportInProgress(false);
  };

  try {
    // 1) local do arquivo, ainda dentro do clique do usuário
    let handle = null;
    if (typeof window.showSaveFilePicker === "function") {
      handle = await pickSaveLocation();
      if (!handle) {
        closeQuietly();
        return;
      }
    }

    // 2) dados, página a página, com progresso no modal; para cada página, produtos e
    //    participantes das vendas vêm em lote (uma requisição), para as outras abas
    const numberOfRequisitions = Math.ceil(countData / limit);
    const allTransactions = [];
    const allProducts = [];
    const allAttendees = [];
    let fields = [];

    for (let i = 1; i <= numberOfRequisitions; i += 1) {
      const response = await getSalesConsolidated({
        token,
        search,
        eventId,
        currentIndex: i,
        itemsPerPage: limit,
        columnSearch,
      });
      const transactions = response?.transactions ?? [];
      allTransactions.push(...transactions);

      const ids = transactions.map((t) => t.id).filter(Boolean);
      if (ids.length > 0) {
        const details = await getSalesExportDetails({ jwt: token, ids });
        allProducts.push(...(details?.products ?? []));
        allAttendees.push(...(details?.attendees ?? []));
        if (Array.isArray(details?.fields) && details.fields.length > 0) {
          fields = details.fields;
        }
      }
      setProgress(i, numberOfRequisitions);
    }

    // 3) arquivo com três abas ligadas pelo ID da venda; sem fileName a biblioteca
    //    devolve o Blob em vez de disparar o download
    const attendeesSheet = buildAttendeesSheet(allAttendees, fields);
    const blob = await writeXlsxFile(
      [allTransactions, allProducts, attendeesSheet.rows],
      {
        schema: [schema, productsSchema, attendeesSheet.schema],
        sheets: ["Vendas", "Produtos", "Participantes"],
      }
    );
    if (handle) await writeToHandle(handle, blob);
    else await downloadWithAnchor(blob);

    // 4) só agora a exportação está de fato concluída
    setFinishExport(true);
  } catch (error) {
    toast.error("Não foi possível exportar as vendas consolidadas");
    closeQuietly();
  }
}
